import {useEffect, useMemo, useRef, useCallback} from 'react';
import {AutoSizer, List, CellMeasurerCache, CellMeasurer} from 'react-virtualized';
import {VirtualizedDataTableProps, VirtualizedRowRendererProps, VirtualizedDataTableAPI} from './types';
import './virtualized-data-table.scss';

export default function VirtualizedDataTable<TRow = any>(props: VirtualizedDataTableProps<TRow>) {
    const {
        rows,
        estimatedRowSize = 53,
        overscanRowCount = 5,
        height,
        scrollToIndex,
        scrollToAlignment,
        rowRenderer,
        cacheKey = 0,
        onReady
    } = props;

    const listRef = useRef<List>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const rafPendingRef = useRef(false);

    // Create cache internally - recreated when cacheKey changes
    const cache = useMemo(() => new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: estimatedRowSize,
        minHeight: estimatedRowSize - 1
    }), [cacheKey, estimatedRowSize]);

    // Remeasure function
    const remeasure = useCallback(() => {
        if (cache && listRef.current) {
            cache.clearAll();
            listRef.current.recomputeRowHeights();
            listRef.current.forceUpdateGrid();
        }
    }, [cache]);

    // Expose API to parent via callback
    useEffect(() => {
        if (onReady) {
            const api: VirtualizedDataTableAPI = {remeasure};
            onReady(api);
        }
    }, [onReady, remeasure]);

    // Double RAF for initial render - perfect CSS timing
    // Combined effect to avoid race conditions during pagination
    useEffect(() => {
        if (rows.length > 0 && !rafPendingRef.current) {
            rafPendingRef.current = true;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    remeasure();
                    rafPendingRef.current = false;
                });
            });
        }
    }, [rows.length, remeasure]);

    // ResizeObserver for automatic remeasurement on size changes
    // Catches: CSS application, font loading, zoom, window resize
    useEffect(() => {
        if (!containerRef.current || rows.length === 0) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            // Debounce to avoid excessive recalculations
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }

            resizeTimeoutRef.current = setTimeout(() => {
                remeasure();
            }, 50);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [rows.length, remeasure]);

    // Calculate row height with ceiling and border
    const getRowHeight = ({index}: {index: number}) => {
        const height = cache.rowHeight({index});
        return Math.ceil(height) + 1;
    };

    // Render individual row using parent's renderer - wrapped in CellMeasurer
    const renderRow = ({index, style, key, parent}: {index: number; style: any; key: string; parent: any}) => {
        const row = rows[index];

        return (
            <CellMeasurer
                cache={cache}
                columnIndex={0}
                key={`${key}-${cacheKey}`}
                parent={parent}
                rowIndex={index}
            >
                {({registerChild}) => {
                    const content = rowRenderer({
                        index,
                        row,
                        style,
                        key
                    });

                    // Wrap consumer's content with measurement wrapper
                    return (
                        <div
                            ref={registerChild as any}
                            style={{
                                ...style,
                                boxSizing: 'border-box',
                                height: style.height ? `${parseFloat(style.height as string)}px` : 'auto'
                            }}
                        >
                            {content}
                        </div>
                    );
                }}
            </CellMeasurer>
        );
    };

    // Convert height prop to number if it's a string like "100%"
    const containerHeight = typeof height === 'string' ? height : `${height}px`;

    return (
        <div ref={containerRef} className='virtualized-data-table' style={{height: containerHeight}}>
            <AutoSizer>
                {({height: autoHeight, width}: {height: number; width: number}) => (
                    <List
                        key={cacheKey}
                        ref={listRef}
                        height={autoHeight - 10}
                        rowHeight={getRowHeight}
                        deferredMeasurementCache={cache}
                        rowCount={rows.length}
                        width={width}
                        overscanRowCount={overscanRowCount}
                        estimatedRowSize={estimatedRowSize}
                        rowRenderer={renderRow}
                        scrollToIndex={scrollToIndex}
                        scrollToAlignment={scrollToAlignment}
                    />
                )}
            </AutoSizer>
        </div>
    );
}

export type {VirtualizedDataTableProps, VirtualizedRowRendererProps, VirtualizedDataTableAPI};
